import { Link, useOutletContext } from "react-router-dom";
import { ArrowRight, CheckCircle2, Circle, Clock, ChevronRight } from "lucide-react";
import { FadeUp, SectionTag } from "../../components/site/Reveal";

export function StartHere() {
  const { content, progress, user } = useOutletContext();
  const pct = progress.total_items ? Math.round((progress.completed_count / progress.total_items) * 100) : 0;
  const next = content.modules.find((m) => !m.locked && progress.per_module[m.id]?.done < progress.per_module[m.id]?.total) || content.modules.find((m) => !m.locked) || content.modules[0];

  return (
    <div data-testid="hub-start-here" className="max-w-4xl">
      <FadeUp>
        <SectionTag dark>WorkBase Hub // Orientation</SectionTag>
        <h1 className="font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl mt-4">
          Welcome, operator{user.name ? ` — ${user.name.split(" ")[0]}` : ""}.
        </h1>
        <p className="text-slate-400 mt-3 leading-relaxed max-w-2xl">
          This is your build site. Seven modules, one per week, each ending with a working
          system installed in your business. Everything you check off is saved.
        </p>
      </FadeUp>

      <FadeUp delay={0.1} className="mt-8 bg-charcoal border border-white/10 p-6 sm:p-8">
        <div className="flex items-center justify-between mb-3">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">[OVERALL COMPLETION]</p>
          <p className="font-mono text-safety font-bold" data-testid="hub-overall-pct">{pct}%</p>
        </div>
        <div className="h-2 bg-navy overflow-hidden">
          <div className="h-full bg-safety transition-all duration-700" style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-slate-400 text-sm">Your next move</p>
            <p className="font-display font-bold text-white text-lg">{next.code} — {next.title}</p>
          </div>
          <Link to={`/hub/modules/${next.id}`} data-testid="hub-continue-btn"
            className="bg-safety hover:bg-safety-hover text-white font-semibold px-6 py-3 flex items-center gap-2 text-sm transition-colors">
            Continue building <ArrowRight size={15} />
          </Link>
        </div>
      </FadeUp>

      <FadeUp delay={0.2} className="mt-8 grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10">
        {[
          { n: "01", t: "One module per week", d: "Watch the lessons, then build the artifact. Don't binge — build." },
          { n: "02", t: "Bring it to calls", d: "Weekly cohort call + office hours. Stuck beats silent." },
          { n: "03", t: "Check it off", d: "Lessons and tasks tracked in My Progress and the Implementation Dashboard." },
        ].map((s) => (
          <div key={s.n} className="bg-charcoal p-6">
            <p className="font-mono text-safety text-xs">[{s.n}]</p>
            <p className="font-display font-bold text-white mt-2">{s.t}</p>
            <p className="text-slate-400 text-sm mt-2 leading-relaxed">{s.d}</p>
          </div>
        ))}
      </FadeUp>

      <FadeUp delay={0.3} className="mt-8">
        <Link to="/hub/modules" data-testid="hub-browse-modules"
          className="text-safety hover:text-white text-sm font-semibold flex items-center gap-2 transition-colors">
          Browse all 7 modules <ArrowRight size={15} />
        </Link>
      </FadeUp>
    </div>
  );
}

export function MyProgress() {
  const { content, progress } = useOutletContext();
  const pct = progress.total_items ? Math.round((progress.completed_count / progress.total_items) * 100) : 0;

  return (
    <div data-testid="hub-my-progress" className="max-w-4xl">
      <FadeUp>
        <SectionTag dark>Tracking // Completion</SectionTag>
        <h1 className="font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl mt-4">My Progress</h1>
      </FadeUp>
      <FadeUp delay={0.1} className="mt-8 bg-charcoal border border-white/10 p-6 sm:p-8 flex items-center gap-8">
        <div className="relative w-28 h-28 shrink-0">
          <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
            <circle cx="50" cy="50" r="42" fill="none" stroke="#1B2740" strokeWidth="10" />
            <circle cx="50" cy="50" r="42" fill="none" stroke="#FF5500" strokeWidth="10"
              strokeDasharray={`${(pct / 100) * 264} 264`} strokeLinecap="butt" className="transition-all duration-700" />
          </svg>
          <span className="absolute inset-0 grid place-items-center font-mono font-bold text-white text-xl" data-testid="progress-ring-pct">{pct}%</span>
        </div>
        <div>
          <p className="font-display font-bold text-white text-xl" data-testid="progress-counts">
            {progress.completed_count} of {progress.total_items} items complete
          </p>
          <p className="text-slate-400 text-sm mt-1">
            {pct === 100 ? "Blueprint complete — see Graduation." : pct >= 50 ? "Over the hump. Keep the streak." : "Momentum compounds. One item today."}
          </p>
        </div>
      </FadeUp>
      <div className="mt-6 space-y-px">
        {content.modules.map((m, i) => {
          const pm = progress.per_module[m.id];
          const done = pm?.done === pm?.total;
          return (
            <FadeUp key={m.id} delay={i * 0.04}>
              <Link to={`/hub/modules/${m.id}`} data-testid={`progress-module-${m.id}`}
                className="bg-charcoal border border-white/10 hover:border-safety/50 p-5 flex items-center gap-4 transition-colors group">
                {done ? <CheckCircle2 size={20} className="text-emerald-400 shrink-0" /> : <Circle size={20} className="text-slate-600 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between gap-3 items-baseline">
                    <p className="font-display font-bold text-white group-hover:text-safety transition-colors truncate">{m.code} — {m.title}</p>
                    <p className="font-mono text-xs text-slate-500 shrink-0">{pm?.done || 0}/{pm?.total}</p>
                  </div>
                  <div className="h-1 bg-navy mt-2.5 overflow-hidden">
                    <div className="h-full bg-safety transition-all duration-500" style={{ width: `${pm?.total ? (pm.done / pm.total) * 100 : 0}%` }} />
                  </div>
                </div>
              </Link>
            </FadeUp>
          );
        })}
      </div>
    </div>
  );
}

export function Modules() {
  const { content, progress } = useOutletContext();
  return (
    <div data-testid="hub-modules" className="max-w-5xl">
      <FadeUp>
        <SectionTag dark>Curriculum // 7 Systems</SectionTag>
        <h1 className="font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl mt-4">Blueprint Modules</h1>
      </FadeUp>
      <div className="mt-8 grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
        {content.modules.map((m, i) => {
          const pm = progress.per_module[m.id];
          const inner = (
            <>
              <div className="flex justify-between items-start">
                <p className="font-mono text-[11px] tracking-[0.2em] text-safety">[{m.code}]</p>
                {m.locked
                  ? <span className="font-mono text-[10px] uppercase tracking-widest text-amber-300 border border-amber-400/40 bg-amber-400/10 px-2 py-0.5">Locked</span>
                  : <p className="font-mono text-[11px] text-slate-500">{pm?.done || 0}/{pm?.total} done</p>}
              </div>
              <h3 className={`font-display font-bold text-xl mt-3 transition-colors ${m.locked ? "text-slate-500" : "text-white group-hover:text-safety"}`}>{m.title}</h3>
              <p className="text-slate-400 text-sm mt-2 leading-relaxed">{m.tagline}</p>
              {m.locked && (
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-4" data-testid={`module-lock-note-${m.id}`}>
                  {m.unlock_at ? `Unlocks ${new Date(m.unlock_at).toLocaleDateString()}` : "Locked — ask your coach"}
                </p>
              )}
            </>
          );
          return (
            <FadeUp key={m.id} delay={i * 0.05} className="h-full">
              {m.locked ? (
                <div data-testid={`module-card-${m.id}`} className="block bg-charcoal/50 p-7 h-full opacity-70 cursor-not-allowed">{inner}</div>
              ) : (
                <Link to={`/hub/modules/${m.id}`} data-testid={`module-card-${m.id}`}
                  className="block bg-charcoal hover:bg-steel p-7 h-full transition-colors group">{inner}</Link>
              )}
            </FadeUp>
          );
        })}
      </div>
    </div>
  );
}

export function ModuleDetail({ moduleId }) {
  const { content, progress, toggle } = useOutletContext();
  const m = content.modules.find((x) => x.id === moduleId);
  if (!m) return <p className="text-slate-400">Module not found.</p>;
  if (m.locked) {
    return (
      <div data-testid="module-locked" className="max-w-xl">
        <p className="font-mono text-[11px] tracking-[0.2em] text-amber-300">[{m.code} // LOCKED]</p>
        <h1 className="font-display font-extrabold tracking-tight text-white text-3xl mt-3">{m.title}</h1>
        <p className="text-slate-400 mt-4 leading-relaxed">
          This module unlocks on your drip schedule{m.unlock_at ? ` — ${new Date(m.unlock_at).toLocaleDateString()}` : ""}.
          Finished early or need it now? Ask your coach via Ask for Help.
        </p>
        <Link to="/hub/modules" className="inline-block mt-6 text-safety hover:text-white text-sm font-semibold transition-colors">
          ← Back to Blueprint Modules
        </Link>
      </div>
    );
  }
  const done = (id) => progress.completed.includes(`${m.id}:${id}`);

  const Row = ({ item, kind, label, meta }) => (
    <button
      data-testid={`${kind}-${m.id}-${item.id}`}
      onClick={() => toggle(`${m.id}:${item.id}`, !done(item.id))}
      className={`w-full text-left p-4 border flex items-start gap-3.5 transition-colors ${
        done(item.id) ? "border-emerald-500/30 bg-emerald-500/5" : "border-white/10 bg-charcoal hover:border-white/25"
      }`}
    >
      {done(item.id)
        ? <CheckCircle2 size={19} className="text-emerald-400 shrink-0 mt-0.5" />
        : <Circle size={19} className="text-slate-600 shrink-0 mt-0.5" />}
      <div className="flex-1">
        <p className={`text-sm font-medium ${done(item.id) ? "text-slate-400 line-through" : "text-white"}`}>{label}</p>
        {meta && <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-1">{meta}</p>}
      </div>
    </button>
  );

  return (
    <div data-testid={`hub-module-${m.id}`} className="max-w-3xl">
      <FadeUp>
        <p className="font-mono text-[11px] tracking-[0.2em] text-safety">[{m.code}]</p>
        <h1 className="font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl mt-3">{m.title}</h1>
        <p className="text-slate-400 mt-3 leading-relaxed">{m.tagline}</p>
        <div className="mt-5 bg-safety/10 border border-safety/30 p-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-safety mb-1">Outcome you'll install</p>
          <p className="text-sm text-slate-200">{m.outcome}</p>
        </div>
      </FadeUp>
      <FadeUp delay={0.1} className="mt-8">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">[LESSONS]</p>
        <div className="space-y-2">
          {m.lessons.map((l) => (
            <Link key={l.id} to={`/hub/modules/${m.id}/lessons/${l.id}`} data-testid={`lesson-${m.id}-${l.id}`}
              className={`w-full text-left p-4 border flex items-start gap-3.5 transition-colors group ${
                done(l.id) ? "border-emerald-500/30 bg-emerald-500/5" : "border-white/10 bg-charcoal hover:border-safety/50"
              }`}>
              {done(l.id)
                ? <CheckCircle2 size={19} className="text-emerald-400 shrink-0 mt-0.5" />
                : <Circle size={19} className="text-slate-600 shrink-0 mt-0.5" />}
              <div className="flex-1">
                <p className={`text-sm font-medium ${done(l.id) ? "text-slate-400 line-through" : "text-white group-hover:text-safety transition-colors"}`}>{l.title}</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-1 flex items-center gap-1">
                  <Clock size={10} /> {l.minutes} min · {l.steps.length}-step walkthrough
                </p>
              </div>
              <ChevronRight size={16} className="text-slate-600 group-hover:text-safety shrink-0 mt-1 transition-colors" />
            </Link>
          ))}
        </div>
      </FadeUp>
      <FadeUp delay={0.2} className="mt-8">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">[BUILD TASKS]</p>
        <div className="space-y-2">
          {m.tasks.map((t) => <Row key={t.id} item={t} kind="task" label={t.label} />)}
        </div>
      </FadeUp>
    </div>
  );
}
